import { type Application, type ApplicationStatus, type SubcontractorLinkage, type SubcontractorMaster, isWorkmanCardPassing } from "../types";

export function emptySubcontractorLinkage(): SubcontractorLinkage {
  return {
    subcontractorMasterId: null,
    workOrderFileName: null,
    linkedAt: undefined,
  };
}

export function emptySubcontractorMasterDraft(): Omit<SubcontractorMaster, "id" | "createdAt"> {
  return {
    contactPersonName: "",
    firmName: "",
    registeredAddress: "",
    firmRegistrationId: "",
    gstNumber: "",
    contactPhone: "",
    contactEmail: "",
    aadhaarNumber: "",
    bankAccountNumber: "",
    bankIfsc: "",
    bankBranchName: "",
    bankName: "",
    bankAccountName: "",
    cancelChequeFileName: null,
    cancelChequeStored: false,
    aadharDocFileName: null,
    aadharDocStored: false,
    gstCertFileName: null,
    gstCertStored: false,
    udyamMsmeFileName: null,
    udyamMsmeStored: false,
    labourLicenseFileName: null,
    labourLicenseStored: false,
  };
}

export function normalizeSubcontractorAadhaarDigits(s: string): string {
  return String(s ?? "")
    .replace(/\D/g, "")
    .slice(0, 12);
}

export function isValidSubcontractorIfsc(ifsc: string): boolean {
  return /^[A-Z]{4}0[A-Z0-9]{6}$/.test(String(ifsc ?? "").trim().toUpperCase());
}

/** Master record has minimum identity + bank + mandatory KYC documents for linkage. GST number and GST certificate are optional. */
export function isSubcontractorMasterComplete(m: SubcontractorMaster | undefined): boolean {
  if (!m) return false;
  const gst = m.gstNumber.trim().toUpperCase();
  if (gst.length > 0 && !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(gst)) {
    return false;
  }
  const aadhaar = normalizeSubcontractorAadhaarDigits(m.aadhaarNumber);
  return Boolean(
    m.contactPersonName.trim() &&
      m.firmName.trim() &&
      m.contactPhone.trim() &&
      /^[0-9]{12}$/.test(aadhaar) &&
      m.bankAccountName.trim() &&
      m.bankBranchName.trim() &&
      m.bankName.trim() &&
      m.bankAccountNumber.trim() &&
      isValidSubcontractorIfsc(m.bankIfsc) &&
      m.cancelChequeStored &&
      m.aadharDocStored &&
      m.udyamMsmeStored &&
      m.labourLicenseStored,
  );
}

export function findSubcontractorMaster(masters: SubcontractorMaster[], id: string | null): SubcontractorMaster | undefined {
  if (!id) return undefined;
  return masters.find((x) => x.id === id);
}

export function isSubcontractorLinkageComplete(
  link: SubcontractorLinkage,
  master: SubcontractorMaster | undefined
): boolean {
  if (!link.subcontractorMasterId || !isSubcontractorMasterComplete(master)) return false;
  return true;
}

/**
 * Derives pipeline status from validation, HO banking/MOU, and subcontractor linkage.
 * After seeding: missing account/IFSC/branch → `ready_for_icici`; banking complete but no MOU → `ready_for_mou`.
 */
export function deriveApplicationStatus(a: Application, _masters: SubcontractorMaster[]): ApplicationStatus {
  if (a.holdReasons.length > 0) return "on_hold";
  const v = a.validations;
  const coreOk =
    v.nameMatchAcrossSources === true &&
    v.aadhaarVerified === true &&
    v.bankAccountVerified === true &&
    isWorkmanCardPassing(v.workmanCardAcceptable);
  if (!coreOk) return "pending_validation";
  if (!v.aadhaarSeedingConfirmed) return "pending_validation";
  const iciciFieldsComplete =
    Boolean(a.ho.iciciAccountNumber.trim()) &&
    Boolean(a.ho.iciciIfsc.trim()) &&
    Boolean(a.ho.iciciBranch.trim());
  if (!iciciFieldsComplete) return "ready_for_icici";
  const hasMou =
    Boolean(a.ho.mouFileName) ||
    Boolean(a.ho.mouFileStored) ||
    (Array.isArray(a.ho.mouAttachments) && a.ho.mouAttachments.length > 0);
  if (!hasMou) return "ready_for_mou";
  // Linkage is confirmed once the officer clicked "Record subcontractor" (linkedAt set).
  // Do not re-check master completeness here — the completeness gate is enforced at save time
  // in finalizeSubcontractor; re-checking it on every derive would clear a valid linked status
  // whenever a master field is temporarily incomplete.
  if (a.subcontractor.subcontractorMasterId && a.subcontractor.linkedAt) {
    return "subcontractor_linked";
  }
  return "icici_recorded";
}

/**
 * Pipeline / timeline UI: same rules as {@link deriveApplicationStatus}, but ignores holds so
 * completed steps stay green while the dossier badge can still show on hold.
 */
export function effectivePipelineStatus(a: Application, masters: SubcontractorMaster[]): ApplicationStatus {
  const cleared = a.holdReasons.length > 0 ? { ...a, holdReasons: [] as string[] } : a;
  return deriveApplicationStatus(cleared, masters);
}

export function canAccessSubcontractorStep(app: Application): boolean {
  if (app.holdReasons.length > 0) return false;
  const v = app.validations;
  const coreOk =
    v.nameMatchAcrossSources === true &&
    v.aadhaarVerified === true &&
    v.bankAccountVerified === true &&
    isWorkmanCardPassing(v.workmanCardAcceptable) &&
    v.aadhaarSeedingConfirmed;
  const hoComplete =
    Boolean(app.ho.iciciAccountNumber.trim()) &&
    Boolean(app.ho.iciciIfsc.trim()) &&
    Boolean(app.ho.iciciBranch.trim()) &&
    Boolean(
      app.ho.mouFileName ||
        app.ho.mouFileStored ||
        (Array.isArray(app.ho.mouAttachments) && app.ho.mouAttachments.length > 0),
    );
  if (!coreOk || !hoComplete) return false;
  if (app.workDoneBills.some((b) => !b.seedingReVerified)) return false;
  return true;
}
